// src/modules/Users/UsersPage.js

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import { auth, db, firebaseApp } from '../../firebaseConfig';
import { initializeApp, deleteApp } from 'firebase/app';
import {
    getAuth,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import styles from './UsersPage.module.css';

// Common Components
import {
    SearchBar,
    Pagination,
    TableSkeleton,
    EmptyState,
    PageHeader,
    Toast,
    ConfirmModal,
    ComboBox
} from '../../components/common';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit2,
    FiTrash2,
    FiUserPlus,
    FiMail,
    FiCheck,
    FiClock,
    FiAlertCircle,
    FiRefreshCw,
    FiArrowLeft,
    FiArrowRight,
    FiFilter,
    FiEye,
    FiEyeOff,
    FiKey
} from 'react-icons/fi';

const USERS_COLLECTION = 'users';
const ROLES_COLLECTION = 'parameters/data/cargos';

const ERP_ROLES = [
    { value: 'Administracao', label: 'Administração' },
    { value: 'Comercial', label: 'Comercial' },
    { value: 'Diretoria', label: 'Diretoria (Admin)' },
    { value: 'Laboratorio', label: 'Laboratório' },
    { value: 'Logistica', label: 'Logística' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'TI', label: 'TI' },
    { value: 'Inspetor', label: 'Inspetor' },
    { value: 'Operacional', label: 'Operacional' },
    { value: 'Visitante', label: 'Visitante' }
];

const UsersPage = () => {
    const { t } = useTranslation();

    // Read centralized remote action allowances
    const can = usePagePermissions('usuarios');

    // Estados
    const [editingUser, setEditingUser] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState(''); // '' = all roles
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [toast, setToast] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [resetTarget, setResetTarget] = useState(null);
    const [resetLoading, setResetLoading] = useState(false);

    // Campos do formulário
    const [nomeInput, setNomeInput] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [passwordInput, setPasswordInput] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [roleInput, setRoleInput] = useState('Administracao');
    const [cargoInput, setCargoInput] = useState('');

    // Hooks de Dados
    const {
        data: users,
        loading: usersLoading,
        error: usersError,
        updateDocument,
        deleteDocument
    } = useFirestore(USERS_COLLECTION, 'nome');

    const { data: cargos, loading: cargosLoading } = useFirestore(ROLES_COLLECTION, 'name');

    // Funções auxiliares
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleToastClose = () => {
        setToast(null);
    };

    const getCargoName = (cargoId) => {
        return cargos?.find(c => c.id === cargoId)?.name || null;
    };

    const resetFormInputs = () => {
        setNomeInput('');
        setEmailInput('');
        setPasswordInput('');
        setShowPassword(false);
        setRoleInput('Administracao');
        setCargoInput('');
        setFormError('');
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setIsCreating(true);
        resetFormInputs();
    };

    const closeForm = () => {
        setIsCreating(false);
        setEditingUser(null);
        resetFormInputs();
    };

    useEffect(() => {
        if (editingUser) {
            setIsCreating(false);
            setNomeInput(editingUser.nome || '');
            setEmailInput(editingUser.email || '');
            setRoleInput(editingUser.role || ERP_ROLES[0].value);
            setCargoInput(editingUser.cargo || '');
            setPasswordInput('');
            setShowPassword(false);
        }
    }, [editingUser]);

    // Reset pagination whenever filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, roleFilter]);

    // Filtro de busca + filtro por role
    const filteredUsers = users.filter(user => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
            user.nome?.toLowerCase().includes(searchLower) ||
            user.email?.toLowerCase().includes(searchLower) ||
            user.role?.toLowerCase().includes(searchLower);

        const matchesRole = !roleFilter || user.role === roleFilter;

        return matchesSearch && matchesRole;
    });

    // Paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        setFormError('');

        if (passwordInput.length < 6) {
            setFormError(t('pages.users.passwordMinLength'));
            setFormLoading(false);
            return;
        }

        // Create a temporary secondary app using the SAME options as the primary app,
        // so the primary session (the logged-in admin) is never replaced.
        const secondaryApp = initializeApp(
            firebaseApp.options,
            `Secondary-${Date.now()}`
        );
        const secondaryAuth = getAuth(secondaryApp);

        try {
            const userCredential = await createUserWithEmailAndPassword(
                secondaryAuth,
                emailInput,
                passwordInput
            );
            const user = userCredential.user;

            const newUserData = {
                nome: nomeInput,
                email: emailInput,
                role: roleInput,
                cargo: cargoInput || null,
                cargoNome: getCargoName(cargoInput),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                status: 'active'
            };

            // Write the Firestore doc using the PRIMARY db (admin's session)
            const userDocRef = doc(db, USERS_COLLECTION, user.uid);
            await setDoc(userDocRef, newUserData);

            showToast(t('pages.users.createSuccess', { name: nomeInput }), 'success');

            // Clear filters so the new user is visible immediately
            setRoleFilter('');
            setSearchTerm('');

            closeForm();
        } catch (error) {
            console.error('Erro ao criar usuário:', error);
            if (error.code === 'auth/email-already-in-use') {
                setFormError(t('pages.users.emailInUse'));
            } else if (error.code === 'auth/weak-password') {
                setFormError(t('pages.users.passwordMinLength'));
            } else {
                setFormError(t('pages.users.createError', { error: error.message }));
            }
        } finally {
            // Always clean up the secondary app so it doesn't leak
            try { await deleteApp(secondaryApp); } catch (_) { }
            setFormLoading(false);
        }
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        setFormError('');

        if (!editingUser || !editingUser.id) {
            setFormError(t('pages.users.updateError'));
            setFormLoading(false);
            return;
        }

        try {
            const updateData = {
                nome: nomeInput,
                role: roleInput,
                cargo: cargoInput || null,
                cargoNome: getCargoName(cargoInput),
                updatedAt: new Date().toISOString()
            };

            await updateDocument(editingUser.id, updateData);
            showToast(t('pages.users.updateSuccess', { name: nomeInput }), 'success');
            closeForm();
        } catch (error) {
            console.error('Erro ao editar usuário:', error);
            setFormError(t('pages.users.updateError'));
        } finally {
            setFormLoading(false);
        }
    };

    const handleFormSubmit = isCreating ? handleCreateUser : handleEditUser;

    const handleDeleteUser = async (userId, userName) => {
        // Prevent admins from deleting themselves
        if (userId === auth.currentUser?.uid) {
            showToast(t('pages.users.cannotDeleteSelf'), 'error');
            return;
        }

        try {
            await deleteDocument(userId);
            showToast(t('pages.users.deleteSuccess', { name: userName }), 'warning');
        } catch (error) {
            setFormError(t('pages.users.deleteError'));
            console.error('Erro ao deletar usuário:', error);
            showToast(t('pages.users.deleteError'), 'error');
        }
    };

    const handleDeleteClick = (userId, userName) => {
        setDeleteTarget({ id: userId, name: userName });
    };

    const handleConfirmDelete = async () => {
        if (deleteTarget) {
            await handleDeleteUser(deleteTarget.id, deleteTarget.name);
            setDeleteTarget(null);
        }
    };

    // --- Password reset ---
    const handleResetPasswordClick = (user) => {
        if (!user?.email) {
            showToast(t('pages.users.resetPassword.noEmail'), 'error');
            return;
        }
        setResetTarget({ id: user.id, name: user.nome, email: user.email });
    };

    const handleConfirmResetPassword = async () => {
        if (!resetTarget?.email) {
            setResetTarget(null);
            return;
        }

        setResetLoading(true);
        try {
            // Uses the primary auth instance — sending a reset email does NOT
            // affect the currently logged-in admin's session.
            await sendPasswordResetEmail(auth, resetTarget.email);
            showToast(
                t('pages.users.resetPassword.success', { email: resetTarget.email }),
                'success'
            );
        } catch (error) {
            console.error('Erro ao enviar email de redefinição:', error);
            if (error.code === 'auth/user-not-found') {
                showToast(t('pages.users.resetPassword.userNotFound'), 'error');
            } else if (error.code === 'auth/invalid-email') {
                showToast(t('pages.users.resetPassword.invalidEmail'), 'error');
            } else {
                showToast(
                    t('pages.users.resetPassword.error', { error: error.message }),
                    'error'
                );
            }
        } finally {
            setResetLoading(false);
            setResetTarget(null);
        }
    };

    const getRoleLabel = (roleValue) => {
        return ERP_ROLES.find(r => r.value === roleValue)?.label || roleValue || 'Básico';
    };

    const getStatusBadge = (status) => {
        if (status === 'active') {
            return <span className={styles.statusBadgeActive}><FiCheck size={12} style={{ marginRight: '4px' }} /> {t('pages.users.status.active')}</span>;
        }
        return <span className={styles.statusBadgePending}><FiClock size={12} style={{ marginRight: '4px' }} /> {t('pages.users.status.inactive')}</span>;
    };

    const cargoOptions = cargos?.map(cargo => ({
        id: cargo.id,
        label: cargo.name,
        value: cargo.id
    })) || [];

    const renderForm = () => {
        return (
            <div className={styles.modalBackdrop} onClick={closeForm}>
                <div className={styles.formContainer} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <h2>{isCreating ? t('pages.users.newUser') : t('pages.users.editUser')}</h2>
                        <button className={styles.closeButton} onClick={closeForm}><FiX size={24} /></button>
                    </div>
                    <form onSubmit={handleFormSubmit}>
                        <div className={styles.formContent}>
                            <div className={styles.formGroup}>
                                <label>
                                    {t('pages.users.form.name')} <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={nomeInput}
                                    onChange={(e) => setNomeInput(e.target.value)}
                                    required
                                    disabled={formLoading}
                                    className={styles.input}
                                    placeholder={t('pages.users.form.namePlaceholder')}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>
                                    {t('pages.users.form.email')} <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="email"
                                    value={emailInput}
                                    onChange={(e) => setEmailInput(e.target.value)}
                                    required
                                    disabled={formLoading || !isCreating}
                                    className={styles.input}
                                    placeholder={t('pages.users.form.emailPlaceholder')}
                                />
                                {!isCreating && (
                                    <span className={styles.fieldHint}>{t('pages.users.form.emailReadonly')}</span>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label>
                                    {t('pages.users.form.password')} {isCreating && <span className={styles.required}>*</span>}
                                </label>
                                <div className={styles.passwordWrapper}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={passwordInput}
                                        onChange={(e) => setPasswordInput(e.target.value)}
                                        required={isCreating}
                                        disabled={formLoading}
                                        className={`${styles.input} ${styles.passwordInput}`}
                                        placeholder={!isCreating ? t('pages.users.form.passwordEditPlaceholder') : t('pages.users.form.passwordPlaceholder')}
                                    />
                                    <button
                                        type="button"
                                        className={styles.passwordToggle}
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        disabled={formLoading}
                                        aria-label={showPassword ? t('pages.users.form.hidePassword') : t('pages.users.form.showPassword')}
                                        title={showPassword ? t('pages.users.form.hidePassword') : t('pages.users.form.showPassword')}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label>
                                        {t('pages.users.form.role')} <span className={styles.required}>*</span>
                                    </label>
                                    <select
                                        value={roleInput}
                                        onChange={(e) => setRoleInput(e.target.value)}
                                        required
                                        disabled={formLoading}
                                        className={styles.select}
                                    >
                                        {ERP_ROLES.map(role => (
                                            <option key={role.value} value={role.value}>{role.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.users.form.position')}</label>
                                    <ComboBox
                                        options={cargoOptions}
                                        value={cargoInput}
                                        onChange={setCargoInput}
                                        placeholder={t('pages.users.form.positionPlaceholder')}
                                        disabled={formLoading}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.id}
                                        getOptionKey={(option) => option.id}
                                        noResultsMessage={t('pages.users.form.noCargosFound')}
                                    />
                                </div>
                            </div>

                            {formError && <div className={styles.errorHint}><FiAlertCircle size={16} style={{ marginRight: '6px' }} />{formError}</div>}

                            <div className={styles.formActions}>
                                <button type="button" onClick={closeForm} disabled={formLoading} className={styles.cancelButton}>
                                    <FiX size={18} /> {t('pages.users.form.cancel')}
                                </button>
                                <button type="submit" disabled={formLoading} className={styles.confirmButton}>
                                    {formLoading ? (
                                        <>
                                            <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                            {t('pages.users.form.saving')}
                                        </>
                                    ) : (
                                        <><FiSave size={18} /> {t('pages.users.form.save')}</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // Enhanced loading state that waits for permissions
    if (can.loading || usersLoading || cargosLoading) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.loadingOverlay}>
                    <div className={styles.spinner}></div>
                    <p><FiRefreshCw size={16} className={styles.buttonSpinner} style={{ marginRight: '8px' }} /> {t('pages.users.loading')}</p>
                </div>
            </div>
        );
    }

    if (usersError) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}><FiAlertCircle size={48} style={{ color: '#dc3545' }} /></div>
                    <p className={styles.errorText}>{t('pages.users.error', { error: usersError })}</p>
                    <button className={styles.addButton} onClick={() => window.location.reload()}>
                        <FiRefreshCw size={18} /> {t('pages.users.retry')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={handleToastClose}
                />
            )}

            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title={t('pages.users.deleteConfirmTitle')}
                message={t('pages.users.deleteConfirmMessage', { name: deleteTarget?.name || '' })}
                confirmText={t('pages.users.deleteConfirmButton')}
                cancelText={t('pages.users.deleteCancelButton')}
                danger={true}
            />

            <ConfirmModal
                isOpen={!!resetTarget}
                onClose={() => (resetLoading ? null : setResetTarget(null))}
                onConfirm={handleConfirmResetPassword}
                title={t('pages.users.resetPassword.confirmTitle')}
                message={t('pages.users.resetPassword.confirmMessage', {
                    name: resetTarget?.name || '',
                    email: resetTarget?.email || ''
                })}
                confirmText={
                    resetLoading
                        ? t('pages.users.resetPassword.sending')
                        : t('pages.users.resetPassword.confirmButton')
                }
                cancelText={t('pages.users.deleteCancelButton')}
                danger={false}
            />

            {(isCreating || editingUser) && renderForm()}

            <PageHeader
                title={t('pages.users.title')}
                actionText={t('pages.users.newUser')}
                onAction={handleAddUser}
                disabled={!can.create}
                showAction={can.create}
            />

            <div className={styles.searchBar}>
                <SearchBar
                    placeholder={t('pages.users.search.placeholder')}
                    onSearch={setSearchTerm}
                    initialValue={searchTerm}
                    disabled={usersLoading}
                />

                <div className={styles.roleFilterWrapper}>
                    <FiFilter size={16} className={styles.roleFilterIcon} />
                    <select
                        className={styles.roleFilterSelect}
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        disabled={usersLoading}
                        aria-label={t('pages.users.filter.roleLabel')}
                    >
                        <option value="">{t('pages.users.filter.allRoles')}</option>
                        {ERP_ROLES.map(role => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {usersLoading ? (
                <TableSkeleton columns={6} rows={10} />
            ) : currentUsers.length === 0 ? (
                <EmptyState
                    title={
                        (searchTerm || roleFilter)
                            ? t('pages.users.table.emptySearch')
                            : t('pages.users.table.empty')
                    }
                    message={
                        (searchTerm || roleFilter)
                            ? t('pages.users.table.emptySearchMessage')
                            : t('pages.users.table.emptyMessage')
                    }
                    actionText={can.create ? t('pages.users.newUser') : undefined}
                    onAction={can.create ? handleAddUser : undefined}
                />
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{t('pages.users.table.user')}</th>
                                <th>{t('pages.users.table.email')}</th>
                                <th>{t('pages.users.table.position')}</th>
                                <th>{t('pages.users.table.permission')}</th>
                                <th>{t('pages.users.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentUsers.map((user) => (
                                <tr key={user.id} className={user.status === 'inactive' ? styles.inactiveRow : ''}>
                                    <td>
                                        <div className={styles.userInfo}>
                                            <div>
                                                <div className={styles.userName}>{user.nome || 'N/A'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td><FiMail size={14} style={{ marginRight: '6px', color: 'var(--text-secondary)' }} />{user.email || 'N/A'}</td>
                                    <td>
                                        {user.cargoNome ? (
                                            <span className={styles.tag}>{user.cargoNome}</span>
                                        ) : (
                                            <span className={styles.textMuted}>{t('pages.users.table.notDefined')}</span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={styles.roleBadge}>
                                            {getRoleLabel(user.role)}
                                        </span>
                                    </td>
                                    <td className={styles.actionsCell}>
                                        {can.edit && (
                                            <button
                                                onClick={() => setEditingUser(user)}
                                                className={`${styles.actionButton} ${styles.editButton}`}
                                                disabled={formLoading}
                                                title={t('common.edit')}
                                            >
                                                <FiEdit2 size={14} />
                                            </button>
                                        )}

                                        {can.edit && (
                                            <button
                                                onClick={() => handleResetPasswordClick(user)}
                                                className={`${styles.actionButton} ${styles.resetPasswordButton}`}
                                                disabled={formLoading || !user.email}
                                                title={t('pages.users.resetPassword.buttonTitle')}
                                            >
                                                <FiKey size={14} />
                                            </button>
                                        )}

                                        {can.delete && (
                                            <button
                                                onClick={() => handleDeleteClick(user.id, user.nome)}
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                disabled={formLoading}
                                                title={t('common.delete')}
                                            >
                                                <FiTrash2 size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredUsers.length}
                onPageChange={handlePageChange}
                disabled={usersLoading}
                itemsLabel={t('pages.users.pagination.userPlural')}
            />
        </div>
    );
};

export default UsersPage;